import React, { useCallback, useEffect, useRef, useState } from "react";
import { DownArrow } from "shared-components/Components/CustomIcons/DownArrow/web";
import { useClickAway } from "shared-components/GlobalHooks/useClickAway";
import { Arrow } from "src/Components/ViewFragments/AboutUs/OptionsLabel";
import type { NavCityItem } from "./fincaraizNav.config";

type NavCityDropdownProps = {
	label: string;
	cities: NavCityItem[];
	seeAllHref: string;
	seeAllLabel?: string;
	variant?: "default" | "simple";
	isMobile?: boolean;
	linkClass?: string;
	countryCode?: string;
	isOpen?: boolean;
	onToggle?: () => void;
	onClose?: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
};

export const NavCityDropdown = ({
	label,
	cities,
	seeAllHref,
	seeAllLabel = "Ver todas las ciudades →",
	variant = "default",
	isMobile = false,
	linkClass = "header_btn",
	countryCode = "CO",
	isOpen: controlledOpen,
	onToggle,
	onClose,
	onMouseEnter,
	onMouseLeave,
}: NavCityDropdownProps) => {
	const [internalOpen, setInternalOpen] = useState(false);
	const isControlled = controlledOpen !== undefined;
	const open = isControlled ? controlledOpen : internalOpen;
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const close = useCallback(() => {
		if (isControlled) {
			onClose?.();
		} else {
			setInternalOpen(false);
		}
	}, [isControlled, onClose]);

	const toggle = useCallback(() => {
		if (isControlled) {
			onToggle?.();
		} else {
			setInternalOpen(prev => !prev);
		}
	}, [isControlled, onToggle]);

	const containerRef = useClickAway(() => {
		if (open) close();
	});

	useEffect(() => {
		if (!open || isMobile) return;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [open, isMobile, close]);

	const clearHoverTimeout = () => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
	};

	const handleMouseEnter = () => {
		if (isMobile) return;
		if (isControlled) {
			onMouseEnter?.();
		} else {
			clearHoverTimeout();
			setInternalOpen(true);
		}
	};

	const handleMouseLeave = () => {
		if (isMobile) return;
		if (isControlled) {
			onMouseLeave?.();
		} else {
			clearHoverTimeout();
			hoverTimeoutRef.current = setTimeout(() => close(), 120);
		}
	};

	useEffect(() => () => clearHoverTimeout(), []);

	const isSimple = variant === "simple";

	const panelContent = isSimple ? (
		<>
			<ul className="nav-city-dropdown__cities nav-city-dropdown__cities--3-cols nav-city-dropdown__cities--simple">
				{cities.map(city => (
					<li key={city.href} className="nav-city-dropdown__city-group">
						<a className="nav-city-dropdown__city-link" href={city.href} onClick={close}>
							{city.label}
						</a>
					</li>
				))}
				{cities.flatMap(city =>
					city.subLinks?.map(link => (
						<li key={link.href} className="nav-city-dropdown__simple-sublink-cell">
							<a
								className="nav-city-dropdown__simple-sublink"
								href={link.href}
								onClick={close}
							>
								{link.label}
							</a>
						</li>
					)) ?? []
				)}
			</ul>
			<div className="nav-city-dropdown__see-all-bar">
				<a href={seeAllHref} onClick={close}>
					{seeAllLabel}
				</a>
			</div>
		</>
	) : (
		<>
			<div className="nav-city-dropdown__cities nav-city-dropdown__cities--3-cols nav-city-dropdown__cities--mega">
				{cities.map(city => (
					<div key={city.href} className="nav-city-dropdown__city-group">
						<a className="nav-city-dropdown__city-link" href={city.href} onClick={close}>
							{city.label}
						</a>
						<ul className="nav-city-dropdown__city-sublinks">
							{city.subLinks?.map(link => (
								<li key={link.href}>
									<a
										className="nav-city-dropdown__sublink"
										href={link.href}
										onClick={close}
									>
										{link.label}
									</a>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
			<div className="nav-city-dropdown__see-all-bar">
				<a href={seeAllHref} onClick={close}>
					{seeAllLabel}
				</a>
			</div>
		</>
	);

	const panelClassName = isSimple
		? "nav-city-dropdown__panel nav-city-dropdown__panel--simple"
		: "nav-city-dropdown__panel nav-city-dropdown__panel--mega";

	if (isMobile) {
		return (
			<div className="nav-city-dropdown nav-city-dropdown--mobile">
				<div
					className="nav-city-dropdown__trigger nav-city-dropdown__trigger--mobile"
				>
					<a
						href={seeAllHref}
						className="nav-city-dropdown__trigger-link"
						onClick={close}
					>
						{label}
					</a>
					<button
						type="button"
						className="nav-city-dropdown__trigger-toggle"
						onClick={toggle}
						aria-expanded={open}
						aria-label={`${open ? "Cerrar" : "Abrir"} menu ${label}`}
					>
						<Arrow style={{ transform: `rotate(${open ? "90deg" : "0deg"})`, transition: "all 0.2s" }} />
					</button>
				</div>
				{open && (
					<div className={`${panelClassName} nav-city-dropdown__panel--mobile`}>
						{panelContent}
					</div>
				)}
			</div>
		);
	}

	return (
		<div
			className={`nav-city-dropdown ${open ? "nav-city-dropdown--open" : ""}`}
			ref={containerRef}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<a
				href={seeAllHref}
				className={`${linkClass} ${countryCode} nav-city-dropdown__trigger`}
				aria-expanded={open}
				aria-haspopup="true"
			>
				{label}
				<DownArrow width={12} height={12} />
			</a>
			{open && <div className={panelClassName}>{panelContent}</div>}
		</div>
	);
};
